import { ManagedSelect } from "components/orders/Fields/Select/ManagedSelect";
import { ManagedToggle } from "components/orders/Fields/Toggle/ManagedToggle";
import { isOptionsHidden } from "components/orders/helpers/FeatureHelper";
import type { projectOrderState } from "context/OrderContext";
import { FeatureType } from "models";
import { DivingBoardFastenType } from "models/Feature";
import type { FeatureMeasurements } from "models/Project";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import ImageGallery from "react-image-gallery";
import type { ImageType } from "react-images-uploading";
import type { Visibility } from "types";
import {
  anchorTypeOptions,
  ANCHOR_NA,
  isFeatureRemovable,
  NO,
  YES,
  removableTypeOptions,
  featureInOverlap,
  coverExtensionOptions,
  EXTEND_MATERIAL_OVER_AUTOCOVER_BOX,
  EXTEND_STRAPS_BEYOND_AUTOCOVER_BOX,
} from "./FeatureConstants";
import type { BaseDeckFormType } from "./FeatureSchema";
import "./Feature.scss";
import type { FeatureFormFieldValidationStatus } from "../FeaturesForm/FeaturesForm";
import { ManagedInput } from "components/orders/Fields/Input/ManagedInput";
import { useT } from "i18n";
import CoverExtensionWidthInput from "./CoverExtensionWidthInput/CoverExtensionWidthInput";

interface CoverFeatureProps {
  name: string;
  title: string;
  type: FeatureType;
  notes?: string;
  fieldNotes?: string;
  featureNotes?: string;
  setFeature: (feature: BaseDeckFormType) => void;
  validFields: FeatureFormFieldValidationStatus;
  feature: FeatureValuesType;
  images: ImageType[];
  archived?: boolean;
  measurement?: FeatureMeasurements;
  project: projectOrderState;
  hasYStrap?: boolean;
  yStrapQty?: string;
  divingBoardFastenType?: DivingBoardFastenType;
  // auto cover related fields
  coverExtension?: string;
  coverExtensionWidth?: string;
  coverExtensionStrapLength?: number;
}

export interface FeatureValuesType {
  id: string;
  anchorTypeOptions?: string;
  cableAssembly?: boolean;
  isRemovable?: boolean;
  isCovered?: boolean;
  notes?: string;
  coverExtension?: string;
}

const DivingBoardOverlapWarning = () => {
  const t = useT();
  return (
    <span className={"featureContainer"}>
      <i className={"featureIcon exclamation triangle icon"} />
      <div className={"featureWarning"}>
        {t(
          "component.feature.divingBoardOverlap",
          "The diving board is within the overlap and will require a cutout.",
        )}
      </div>
    </span>
  );
};

export const CoverFeature = ({
  name,
  title,
  type,
  notes,
  fieldNotes,
  featureNotes,
  feature,
  setFeature,
  validFields,
  images,
  archived,
  measurement,
  project,
  hasYStrap,
  yStrapQty,
  divingBoardFastenType,
  coverExtension,
  coverExtensionWidth,
  coverExtensionStrapLength,
}: CoverFeatureProps) => {
  const t = useT();
  const isRemovableInputEnabled = isFeatureRemovable(type);
  const isOther = type === FeatureType.Other;
  const isSpa = type === FeatureType.Spa;
  const isRock = type === FeatureType.DivingObject; // Rock
  const isCutout = type === FeatureType.DivingBoard; // Diving Board
  const isWall = type === FeatureType.Wall;
  const isPlanter = type === FeatureType.Planter; // Planter Pot
  const isAutoCover = type === FeatureType.AutoCover; // Auto Cover Feature
  const isInOverlap = featureInOverlap(isCutout, project, measurement);

  const hideAnchorCableOptions = isOptionsHidden(type);
  const naAnchor = !isRemovableInputEnabled && !hideAnchorCableOptions;

  // N/A is not a valid option for Wall features
  const translateFeatureOptionLabel = (value: string, label: string): string => {
    const translationMap: Record<string, string> = {
      [ANCHOR_NA]: "component.featureConstants.notApplicable",
      "SS Flush Wall Anchor": "component.featureConstants.ssFlushWallAnchor",
      '2" Thunder Bolt': "component.featureConstants.twoInchThunderBolt",
      '6" Thunder Bolt': "component.featureConstants.sixInchThunderBolt",
      [YES]: "component.featureConstants.yes",
      [NO]: "component.featureConstants.no",
    };
    const translationKey = translationMap[value];
    return translationKey ? t(translationKey, label) : label;
  };

  const translateFeatureOptions = (
    options: Array<{ label: string; value: string }>,
  ): Array<{ label: string; value: string }> => {
    return options.map((option) => ({
      ...option,
      label: translateFeatureOptionLabel(option.value, option.label),
    }));
  };

  const anchorOptions = isWall
    ? anchorTypeOptions.slice(1, anchorTypeOptions.length)
    : anchorTypeOptions;

  // Field states
  const [anchorType, setAnchorType] = useState<string | undefined>(
    naAnchor ? (feature?.anchorTypeOptions ?? undefined) : ANCHOR_NA,
  );

  const [cableAssembly, setCableAssembly] = useState(feature?.cableAssembly ?? false);

  const [isRemovable, setIsRemovable] = useState(feature?.isRemovable ? YES : NO);

  const [isCovered, setIsCovered] = useState(feature?.isCovered ? YES : NO);

  const [showAnchorType, setShowAnchorType] = useState(!feature?.isCovered);

  const [featNotes, setFeatNotes] = useState(feature?.notes ?? notes);

  const [appNote, setAppNotes] = useState<string | undefined>(fieldNotes);
  const [portalNotes, setPortalNotes] = useState<string | undefined>(featureNotes);

  const [coverExtensionState, setCoverExtensionState] = useState(
    coverExtension ?? EXTEND_MATERIAL_OVER_AUTOCOVER_BOX,
  );
  const [coverExtensionWidthState, setCoverExtensionWidthState] = useState(
    coverExtensionWidth ?? "",
  );
  // Not to be confused with y straps, these are straps that slide through the cover and hold the cover up
  const [coverExtensionStrapLengthState, setCoverExtensionStrapLengthState] = useState(
    coverExtensionStrapLength ?? 18,
  ); // begins at 18"
  const [hasYStrapState, setHasYStrapState] = useState<boolean>(hasYStrap ?? false);
  const [yStrapQtyState, setYStrapQtyState] = useState<string | undefined>(yStrapQty);

  const [galleryVisible, setIsGalleryVisible] = useState(false);
  const gallery = useRef<ImageGallery | null>(null);

  const [visibility, setVisibility] = useState<Visibility>("hidden");
  const [style, setStyle] = useState<any>({
    boxShadow: "-1px 1px 8px rgb(0 0 0 / 41%)",
  });
  const [divingBoardFasten, setDivingBoardFasten] = useState<string | undefined>(
    divingBoardFastenType,
  );

  const getDivingBoardFastenTypeText = (type?: DivingBoardFastenType) => {
    if (!type) return "";
    if (type === DivingBoardFastenType.MoldedBase) {
      return t("component.feature.moldedBase", "Molded Base");
    } else if (type === DivingBoardFastenType.TubesOrRails) {
      return t(
        "component.feature.tubesOrRails",
        "Tubes or Rails(cutouts are required)",
      );
    }
  };

  const onImageClick = (
    ref: React.MutableRefObject<ImageGallery | null>,
    index: number,
  ) => {
    ref.current?.slideToIndex(index);
    ref.current?.fullScreen();
  };

  useEffect(() => {
    if (!name && !cableAssembly && !anchorType) {
      return;
    }

    setFeature({
      ...feature,
      anchorTypeOptions: anchorType,
      cableAssembly: cableAssembly,
      isRemovable: isRemovable === YES,
      isCovered: isCovered === YES,
      featureName: name,
      notes: featNotes,
      id: feature.id,
      hasYStrap: hasYStrapState,
      yStrapQty: Number(yStrapQtyState || "1"),
      divingBoardFastenType: divingBoardFastenType,
      // auto cover related fields
      coverExtension: coverExtensionState,
      coverExtensionWidth: coverExtensionWidthState,
      coverExtensionStrapLength: coverExtensionStrapLengthState,
    } as BaseDeckFormType);
  }, [
    anchorType,
    cableAssembly,
    isRemovable,
    isCovered,
    featNotes,
    hasYStrapState,
    yStrapQtyState,
    divingBoardFastenType,
    coverExtensionState,
    coverExtensionWidthState,
    coverExtensionStrapLengthState,
  ]);

  useEffect(() => {
    if (portalNotes && portalNotes != "null") {
      setFeatNotes(appNote + ". " + portalNotes);
    } else {
      setFeatNotes(appNote);
    }
  }, [portalNotes]);

  useEffect(() => {
    setShowAnchorType(isCovered === NO);
    if (isSpa) {
      setAnchorType(isCovered !== NO ? ANCHOR_NA : anchorType);
    }
  }, [isCovered]);

  useEffect(() => {
    if (isRemovable === YES && !isOther) {
      setAnchorType(ANCHOR_NA);
    }
  }, [isRemovable]);

  useEffect(() => {
    if (!hasYStrapState) {
      setYStrapQtyState("1");
    }
  }, [hasYStrapState]);

  const setHoverStyle = () => {
    setVisibility("visible");
    setStyle({
      cursor: "pointer",
      background: "#fff",
      border: "none",
      webkitBoxShadow: "0 1px 3px 0 #bcbdbd,0 0 0 1px #d4d4d5",
      boxShadow: "0 1px 3px 0 #bcbdbd,0 0 0 1px #d4d4d5",
      webkitTransform: "translateY(-2px)",
      transform: "translateY(-2px)",
    });
  };

  const clearHoverStyle = () => {
    setVisibility("hidden");
    setStyle({
      boxShadow: "-1px 1px 8px rgb(0 0 0 / 41%)",
    });
  };

  // Ensure diving board message is not shown if diving board feature is removable
  const showDivingBoardOverlapWarning = isInOverlap && isRemovable === NO;

  const strapsExtendBeyondAutocoverBox =
    coverExtensionState === EXTEND_STRAPS_BEYOND_AUTOCOVER_BOX;
  const strapsCoverFullWidthOfPool = coverExtensionWidthState === "fullPoolWidth";

  return (
    <div className={"feature"}>
      <div className={"feature__title"}>{title}</div>
      {showDivingBoardOverlapWarning && <DivingBoardOverlapWarning />}
      {images.length > 0 ? (
        <>
          <div
            className={"image-container-large"}
            onClick={() => onImageClick(gallery, 0)}
            onMouseEnter={() => setHoverStyle()}
            onMouseLeave={() => clearHoverStyle()}
          >
            <img
              src={images[0]?.url}
              className={"feature__image"}
              style={style}
              onClick={() => onImageClick(gallery, 0)}
            />
            <div
              className={"icon-container-large"}
              style={{
                visibility: visibility,
              }}
            >
              <i className={"zoom-in icon"} />
            </div>
          </div>
          <ImageGallery
            lazyLoad={true}
            ref={gallery}
            items={images.map((img: any) => ({
              original: img?.url,
              thumbnail: img?.url,
            }))}
            useBrowserFullscreen={false}
            onScreenChange={setIsGalleryVisible}
            thumbnailPosition={"top"}
            additionalClass={"abs hidden " + (galleryVisible ? "show" : "")}
            showPlayButton={false}
            showFullscreenButton={true}
          />
        </>
      ) : (
        <div>{t("component.common.noImageFound", "No image found.")}</div>
      )}
      <ManagedInput
        label={t("component.feature.fieldNotes", "Field Notes")}
        isValid={validFields.notes}
        type={"text"}
        placeholder={t("component.feature.fieldNotes", "Field Notes")}
        onChange={setAppNotes}
        value={appNote}
        disabled={true}
      />
      <ManagedInput
        label={t("component.feature.featureNotes", "Feature Notes")}
        isValid={true}
        type={"text"}
        placeholder={t("component.feature.featureNotes", "Feature Notes")}
        onChange={setPortalNotes}
        value={portalNotes}
        disabled={archived}
      />
      {isCutout && divingBoardFasten && (
        <>
          <ManagedInput
            label={t(
              "component.feature.howIsDivingBoardFastened",
              "How is the Diving Board fastened?",
            )}
            isValid={true}
            placeholder={""}
            onChange={() => null}
            value={getDivingBoardFastenTypeText(divingBoardFastenType)}
            disabled={true}
          />
        </>
      )}
      {isAutoCover && (
        <>
          <ManagedSelect
            label={"Safety Cover Options (Autocover)"}
            isValid={validFields.coverExtension}
            value={coverExtensionState}
            onChange={(value: string) => {
              setCoverExtensionState(value);
            }}
            options={coverExtensionOptions}
            name={"cover_extension"}
            disabled={archived}
          />
          {strapsExtendBeyondAutocoverBox && (
            <ManagedSelect
              label={"Cover Extension Width"}
              isValid={validFields.coverExtensionWidth}
              value={coverExtensionWidthState}
              onChange={setCoverExtensionWidthState}
              options={[
                { label: "Full Width of Pool", value: "fullPoolWidth" },
                {
                  label: "Extension strap for Autocover motor only",
                  value: "onlyMotorWidth",
                },
              ]}
              name={"cover_extension_width"}
              disabled={archived}
            />
          )}
          {strapsExtendBeyondAutocoverBox && strapsCoverFullWidthOfPool && (
            <CoverExtensionWidthInput
              min={18}
              max={48}
              step={2}
              value={coverExtensionStrapLengthState}
              onChange={(value) => {
                setCoverExtensionStrapLengthState(value);
              }}
              label={"Value"}
            />
          )}
        </>
      )}
      {isSpa && (
        <>
          <ManagedSelect
            label={t("component.feature.isSpaCovered", "Is Spa Covered?")}
            isValid={validFields.isCovered}
            value={isCovered}
            onChange={setIsCovered}
            options={translateFeatureOptions(removableTypeOptions)}
            name={"is_covered"}
            disabled={archived}
          />
        </>
      )}
      {isRock && (
        <>
          <ManagedSelect
            label={t(
              "component.feature.doesObjectExtendBelowDeck",
              "Does object extend below deck?",
            )}
            isValid={validFields.isCovered}
            value={isCovered}
            onChange={setIsCovered}
            options={translateFeatureOptions(removableTypeOptions)}
            name={"is_extends_below"}
            disabled={archived}
          />
        </>
      )}
      {(isRemovableInputEnabled || isOther) && (
        <>
          <ManagedSelect
            label={t("component.feature.isFeatureRemovable", "Is Feature Removable?")}
            isValid={validFields.isRemovable}
            value={isRemovable}
            onChange={setIsRemovable}
            options={translateFeatureOptions(removableTypeOptions)}
            name={"is_removable"}
            disabled={archived}
          />
        </>
      )}
      {(isRemovable === NO || isOther) && !hideAnchorCableOptions && (
        <>
          {((isSpa && showAnchorType) || !isSpa) && (
            <ManagedSelect
              label={t("component.feature.anchorType", "Anchor Type")}
              isValid={validFields.anchorTypeOptions}
              value={anchorType}
              onChange={setAnchorType}
              options={translateFeatureOptions(anchorOptions)}
              name={"deck_type"}
              disabled={archived || !naAnchor}
            />
          )}
          <ManagedToggle
            label={t("component.feature.cableAssembly", "Cable Assembly")}
            name={"cableAssembly"}
            isValid={validFields.cableAssembly}
            onChange={setCableAssembly}
            checked={cableAssembly}
            disabled={archived}
          />
        </>
      )}

      {/* Y-strap components for specific feature types */}
      {(isPlanter || isRock || isCutout) && (
        <>
          <ManagedToggle
            label={"Y Strap"}
            checked={hasYStrapState}
            name={"hasYStrap"}
            onChange={setHasYStrapState}
            isValid={validFields.hasYStrap}
          />
          {hasYStrapState && (
            <ManagedInput
              label={"How Many?"}
              isValid={validFields.yStrapQty}
              type={"number"}
              min={1}
              placeholder={"Strap Quantity"}
              onChange={setYStrapQtyState}
              value={yStrapQtyState}
              disabled={archived}
            />
          )}
        </>
      )}
    </div>
  );
};
